import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, query, where, orderBy, getDocs, getDoc, doc, 
    serverTimestamp, writeBatch, documentId, updateDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    CalendarIcon, EditIcon, StarIcon, TrashIcon, XIcon, 
    CheckCircleIcon, ChatAltIcon, BriefcaseIcon, UserGroupIcon, UserIcon 
} from '../../components/icons/Icons';

// Helper for Avatars
const getAvatar = (name, url) => {
    if (url && url.length > 5) return url;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=random&color=fff&size=128`;
};

const UserAvatar = ({ url, name, className = "h-6 w-6" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return <img src={url} alt={name} className={`${className} rounded-full object-cover border border-gray-200`} onError={() => setImgError(true)} />;
    }
    return (
        <div className={`${className} rounded-full bg-blue-100 flex items-center justify-center text-[#213368] font-bold text-[10px] shrink-0 overflow-hidden border border-blue-200`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-3 w-3" />}
        </div>
    );
};

export default function CompanyTrainingHub({ userProfile, appId }) {
    const [sessions, setSessions] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [attendanceSession, setAttendanceSession] = useState(null);
    const [feedbackSession, setFeedbackSession] = useState(null);
    const [viewFeedbackSession, setViewFeedbackSession] = useState(null);
    const [timeFilter, setTimeFilter] = useState('30days');
    
    const isHR = userProfile.role === 'HR' || userProfile.role === 'Admin' || userProfile.department === 'HR';
    // --- LOAD SESSIONS ---
    const fetchSessions = async () => {
        try {
            const ref = collection(db, `artifacts/${appId}/pd_events`);
            const q = query(
                ref, 
                where('category', 'in', ['Soft Skills', 'Knowledge Enhancement']), 
                orderBy('date', 'desc')
            );
            const snap = await getDocs(q);
            setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error fetching trainings:", error);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [appId]);

    // --- FILTER LOGIC ---
    const filteredSessions = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);

        return sessions.filter(session => {
            const sessionDate = new Date(session.date.seconds * 1000);
            if (timeFilter === '30days') return sessionDate >= now && sessionDate <= thirtyDaysFromNow;
            if (timeFilter === 'upcoming') return sessionDate >= now;
            if (timeFilter === 'past') return sessionDate < now;
            return true; 
        });
    }, [sessions, timeFilter]);

    // --- ACTIONS ---
    const handleDelete = async (sessionId) => {
        if (window.confirm("Delete this training? This will remove it from all employee dashboards.")) {
            try {
                const batch = writeBatch(db);
                batch.delete(doc(db, `artifacts/${appId}/pd_events`, sessionId));
                
                // Cascade delete linked activities
                const logsQ = query(collection(db, `artifacts/${appId}/pd_activities`), where('linkedEventId', '==', sessionId));
                const logsSnap = await getDocs(logsQ);
                logsSnap.forEach(d => batch.delete(d.ref));
                
                await batch.commit();
                fetchSessions();
            } catch (err) {
                console.error(err);
                alert("Error deleting training.");
            }
        }
    };

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setEditingSession(null);
        fetchSessions();
    };

    const isAttendee = (session) => session.attendeeIds?.includes(userProfile.id);
    const hasGivenFeedback = (session) => session.feedbackLog?.[userProfile.id];

    return (
        <div>
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h3 className="text-lg font-bold text-[#213368]">Company Training Calendar</h3>
                    <p className="text-sm text-gray-500">Formal training sessions organized by HR.</p>
                </div>
                {isHR && (
                    <button 
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-4 py-2 bg-[#20BEC6] text-white text-sm font-bold rounded-lg hover:opacity-90 flex items-center shadow-sm whitespace-nowrap"
                    >
                        <span className="mr-1 text-lg">+</span> Schedule Training
                    </button>
                )}
            </div>

            {/* FILTER BAR */}
            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-2 overflow-x-auto">
                    <FilterButton active={timeFilter === '30days'} onClick={() => setTimeFilter('30days')} label="Next 30 Days" />
                    <FilterButton active={timeFilter === 'upcoming'} onClick={() => setTimeFilter('upcoming')} label="All Upcoming" />
                    <FilterButton active={timeFilter === 'past'} onClick={() => setTimeFilter('past')} label="Past Events" />
                    <FilterButton active={timeFilter === 'all'} onClick={() => setTimeFilter('all')} label="All Time" />
                </div>
            </div>

            {/* SESSION LIST */}
            <div className="space-y-4">
                {filteredSessions.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <p className="text-gray-400 italic">No trainings found for this period.</p>
                    </div>
                )}
                
                {filteredSessions.map(session => (
                    <SessionCard 
                        key={session.id}
                        session={session}
                        isHR={isHR}
                        isAttendee={isAttendee(session)}
                        hasGivenFeedback={hasGivenFeedback(session)}
                        onEdit={() => { setEditingSession(session); setIsCreateModalOpen(true); }}
                        onDelete={() => handleDelete(session.id)}
                        onMarkAttendance={() => setAttendanceSession(session)}
                        onGiveFeedback={() => setFeedbackSession(session)}
                        onViewFeedback={() => setViewFeedbackSession(session)}
                    />
                ))}
            </div>

            {/* --- MODALS --- */}
            {isCreateModalOpen && (
                <CreateEditTrainingModal 
                    appId={appId} 
                    onClose={handleModalClose} 
                    currentUser={userProfile}
                    existingData={editingSession}
                />
            )}
            {attendanceSession && (
                <AttendanceModal 
                    appId={appId} 
                    session={attendanceSession} 
                    onClose={() => { setAttendanceSession(null); fetchSessions(); }} 
                />
            )}
            {feedbackSession && (
                <FeedbackModal 
                    appId={appId} 
                    session={feedbackSession} 
                    userId={userProfile.id}
                    onClose={() => { setFeedbackSession(null); fetchSessions(); }} 
                />
            )}
            {viewFeedbackSession && (
                <ViewFeedbackModal 
                    session={viewFeedbackSession}
                    onClose={() => setViewFeedbackSession(null)}
                />
            )}
        </div>
    );
}

function FilterButton({ active, onClick, label }) {
    return (
        <button 
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                active 
                ? 'bg-[#213368] text-white shadow-md' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
        >
            {label}
        </button>
    );
}

// ------------------------------------------------------------------
// TRAINING CARD
// ------------------------------------------------------------------
function SessionCard({ session, isHR, isAttendee, hasGivenFeedback, onEdit, onDelete, onMarkAttendance, onGiveFeedback, onViewFeedback }) {
    const sessionDate = new Date(session.date.seconds * 1000);
    
    // Normalize dates to Midnight (ignore time) to allow "Today" attendance
    const isFutureDate = (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const eventDay = new Date(sessionDate);
        eventDay.setHours(0, 0, 0, 0);

        return eventDay > today; 
    })();

    const feedbackCount = session.feedbackLog ? Object.keys(session.feedbackLog).length : 0;
    const enrolledCount = session.enrolledIds?.length || 0;
    const attendedCount = session.attendeeIds?.length || 0;

    const handleAttendanceClick = () => {
        if (isFutureDate) {
            alert("⚠️ Cannot mark attendance before the event date.");
            return;
        }
        onMarkAttendance();
    };

    return (
        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm hover:shadow-md transition group relative overflow-hidden">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isFutureDate ? 'bg-[#20BEC6]' : 'bg-gray-300'}`}></div>

            <div className="flex flex-col md:flex-row justify-between pl-2">
                <div className="mb-4 md:mb-0">
                    <div className="flex items-center space-x-3 mb-1">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                            session.category === 'Soft Skills' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                            {session.category}
                        </span>
                        <h4 className="font-bold text-[#213368] text-lg">{session.title}</h4>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-500 mt-2 space-y-1 sm:space-y-0 sm:space-x-4">
                        <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1.5 text-gray-400"/>
                            {sessionDate.toLocaleDateString()}
                        </span>
                        <span>{session.durationHours} Hours</span>
                    </div>

                    <div className="mt-3 text-sm text-gray-600 flex items-center">
                        <BriefcaseIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-medium mr-1">Instructor:</span>
                        {session.isExternalTrainer ? (
                            <span className="text-[#213368] font-bold">{session.externalTrainerName || 'External Partner'}</span>
                        ) : (
                            <div className="flex items-center">
                                {session.presenterNames?.map((name, i) => (
                                    <span key={i} className="inline-flex items-center bg-gray-100 px-2 py-0.5 rounded text-xs mr-1">
                                        <UserAvatar name={name} url={session.presenterPhotos?.[i]} className="w-4 h-4 mr-1" />
                                        {name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {isHR && (
                        <div className="mt-2 text-xs text-gray-400 flex items-center">
                            <UserGroupIcon className="h-3 w-3 mr-1"/>
                            <span>{enrolledCount} Enrolled • {attendedCount} Attended</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-end space-y-2">
                    {isHR && (
                        <div className="flex space-x-2">
                             <button 
                                onClick={handleAttendanceClick} 
                                className={`text-xs font-bold text-white px-3 py-1.5 rounded-lg transition shadow-sm flex items-center ${isFutureDate ? 'bg-green-600/50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                title={isFutureDate ? "Available on event date" : "Mark Attendance"}
                             >
                                <CheckCircleIcon className="h-3 w-3 mr-1" /> Attendance
                            </button>
                            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"><EditIcon className="h-4 w-4" /></button>
                            <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><TrashIcon className="h-4 w-4" /></button>
                        </div>
                    )}

                    {(isHR) && feedbackCount > 0 && (
                        <button onClick={onViewFeedback} className="mt-1 text-xs font-medium text-purple-600 hover:bg-purple-50 px-3 py-1.5 rounded-lg border border-purple-200 flex items-center transition">
                            <ChatAltIcon className="h-3 w-3 mr-1" /> View Feedback ({feedbackCount})
                        </button>
                    )}

                    {isAttendee && !hasGivenFeedback && (
                        <button onClick={onGiveFeedback} className="mt-2 text-xs font-medium text-orange-600 hover:bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200 flex items-center transition">
                            <StarIcon className="h-3 w-3 mr-1" /> Rate Training
                        </button>
                    )}
                    
                    {hasGivenFeedback && (
                        <span className="mt-2 text-xs text-green-600 font-medium px-3 py-1.5 bg-green-50 rounded-lg flex items-center">
                            <CheckCircleIcon className="h-3 w-3 mr-1"/> Feedback Sent
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// CREATE/EDIT MODAL
// ------------------------------------------------------------------
function CreateEditTrainingModal({ appId, onClose, currentUser, existingData }) {
    const [formData, setFormData] = useState({ 
        title: existingData?.title || '', 
        category: existingData?.category || 'Soft Skills',
        date: existingData?.date ? new Date(existingData.date.seconds * 1000).toISOString().split('T')[0] : '', 
        duration: existingData?.durationHours || 2, 
        isExternal: existingData?.isExternalTrainer || false,
        externalName: existingData?.externalTrainerName || ''
    });
    
    const [selectedPresenters, setSelectedPresenters] = useState(
        existingData && !existingData.isExternalTrainer
        ? existingData.presenterIds.map((id, index) => ({ 
            id, name: existingData.presenterNames[index], photoURL: existingData.presenterPhotos?.[index]
          })) 
        : []
    );

    const [enrolledUsers, setEnrolledUsers] = useState([]); 
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [departments, setDepartments] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            const q = query(collection(db, `artifacts/${appId}/users`), where("status", "==", "Approved"));
            const snap = await getDocs(q);
            const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllUsers(users);
            
            const groups = {};
            users.forEach(u => {
                const d = u.department || 'Unassigned';
                if (!groups[d]) groups[d] = [];
                groups[d].push(u);
            });
            setDepartments(groups);

            if (existingData?.enrolledIds) {
                setEnrolledUsers(existingData.enrolledIds);
            }
        };
        fetchUsers();
    }, [appId, existingData]);

    const addPresenter = (user) => {
        if (!selectedPresenters.find(p => p.id === user.id)) {
            setSelectedPresenters([...selectedPresenters, { id: user.id, name: user.name, photoURL: user.photoURL }]);
        }
        setSearchTerm('');
    };

    const toggleEnrollUser = (userId) => {
        if (enrolledUsers.includes(userId)) {
            setEnrolledUsers(enrolledUsers.filter(id => id !== userId));
        } else {
            setEnrolledUsers([...enrolledUsers, userId]);
        }
    };

    const toggleEnrollDept = (dept) => {
        const deptUserIds = departments[dept].map(u => u.id);
        const allSelected = deptUserIds.every(id => enrolledUsers.includes(id));
        if (allSelected) {
            setEnrolledUsers(enrolledUsers.filter(id => !deptUserIds.includes(id)));
        } else {
            const newIds = deptUserIds.filter(id => !enrolledUsers.includes(id));
            setEnrolledUsers([...enrolledUsers, ...newIds]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const batch = writeBatch(db);
            const trainingDate = new Date(formData.date);
            
            // Normalize dates to midnight for comparison
            const isPastEvent = (() => {
                const now = new Date();
                now.setHours(0,0,0,0);
                const tDate = new Date(trainingDate);
                tDate.setHours(0,0,0,0);
                return tDate < now;
            })();

            const payload = {
                title: formData.title,
                category: formData.category,
                date: trainingDate,
                durationHours: Number(formData.duration),
                
                organizerId: currentUser.id,
                organizerName: currentUser.name,
                
                isExternalTrainer: formData.isExternal,
                externalTrainerName: formData.isExternal ? formData.externalName : null,
                presenterIds: formData.isExternal ? [] : selectedPresenters.map(p => p.id),
                presenterNames: formData.isExternal ? [] : selectedPresenters.map(p => p.name),
                presenterPhotos: formData.isExternal ? [] : selectedPresenters.map(p => p.photoURL || null),
                
                enrolledIds: enrolledUsers,
                // ✅ IMPORTANT: Do not include attendeeIds here if editing.
                // We don't want to overwrite the attendance list with stale data from this modal.
                ...(existingData ? {} : { attendeeIds: [] })
            };

            // 1. Save Event Document
            let eventId;
            let currentAttendeeIds = []; // We need the accurate list for status calculation

            if (existingData) {
                eventId = existingData.id;
                
                // ✅ CRITICAL FIX: Fetch the latest event data to ensure we have the most up-to-date attendeeIds.
                // This prevents overwriting a "Completed" status with "Missed" due to stale props.
                const freshEventSnap = await getDoc(doc(db, `artifacts/${appId}/pd_events`, eventId));
                if (freshEventSnap.exists()) {
                    currentAttendeeIds = freshEventSnap.data().attendeeIds || [];
                } else {
                    currentAttendeeIds = existingData.attendeeIds || [];
                }

                batch.update(doc(db, `artifacts/${appId}/pd_events`, eventId), payload);
            } else {
                const newEventRef = doc(collection(db, `artifacts/${appId}/pd_events`));
                eventId = newEventRef.id;
                batch.set(newEventRef, { ...payload, createdAt: serverTimestamp() });
                currentAttendeeIds = [];
            }

            // 2. Handle Linked Activities (Prevent Duplicates)
            const logsRef = collection(db, `artifacts/${appId}/pd_activities`);
            const logsQ = query(logsRef, where('linkedEventId', '==', eventId));
            const logsSnap = await getDocs(logsQ);
            
            const existingLogsMap = {};
            logsSnap.forEach(doc => {
                const data = doc.data();
                if (data.userId) existingLogsMap[data.userId] = doc.ref;
            });

            // 3. Update/Create logs for Enrolled Users
            enrolledUsers.forEach(userId => {
                const hasAttended = currentAttendeeIds.includes(userId);
                
                // ✅ LOGIC CHECK: 
                // 1. If hasAttended (True) -> Completed (Always overrides date checks)
                // 2. If !hasAttended (False) AND isPastEvent (True) -> Missed
                // 3. Else -> Upcoming
                let status = 'Upcoming';
                if (hasAttended) {
                    status = 'Completed';
                } else if (isPastEvent) {
                    status = 'Missed';
                }

		const userObj = allUsers.find(u => u.id === userId);

                const activityData = {
                    userId: userId,
		    managerId: userObj?.managerId || null,                    
		    type: formData.category,
                    title: formData.title,
                    status: status,
                    hours: Number(formData.duration),
                    linkedEventId: eventId,
                    date: trainingDate,
                    ...(existingLogsMap[userId] ? {} : { createdAt: serverTimestamp() })
                };

                if (existingLogsMap[userId]) {
                    batch.update(existingLogsMap[userId], activityData);
                    delete existingLogsMap[userId];
                } else {
                    const newLogRef = doc(db, `artifacts/${appId}/pd_activities`, `${eventId}_${userId}`);
                    batch.set(newLogRef, activityData, { merge: true });
                }
            });

            // 4. Cleanup: Delete logs for users who were Un-enrolled
            Object.values(existingLogsMap).forEach(ref => {
                batch.delete(ref);
            });

            await batch.commit();
            onClose();
        } catch (err) {
            console.error(err);
            alert("Error saving training: " + err.message);
            setIsSaving(false);
        }
    };

    const suggestions = allUsers.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 5);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-[#213368]">{existingData ? 'Edit Training' : 'Schedule Training'}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="h-6 w-6"/></button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 flex flex-col md:flex-row gap-6">
                    <div className="w-full md:w-1/3 space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Title</label>
                            <input className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                                placeholder="e.g. Leadership 101" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                            <select className="w-full border border-gray-300 p-2.5 rounded-lg bg-white outline-none"
                                value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                                <option value="Soft Skills">Soft Skills</option>
                                <option value="Knowledge Enhancement">Knowledge Enhancement</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                            <input className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                                type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Duration (Hrs)</label>
                            <input className="w-full border border-gray-300 p-2.5 rounded-lg outline-none" 
                                type="number" step="0.5" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} required />
                        </div>

                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Instructor</label>
                            <div className="flex space-x-2 mb-2">
                                <label className="flex items-center space-x-1 cursor-pointer text-xs">
                                    <input type="radio" checked={!formData.isExternal} onChange={() => setFormData({...formData, isExternal: false})} /> <span>Internal</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer text-xs">
                                    <input type="radio" checked={formData.isExternal} onChange={() => setFormData({...formData, isExternal: true})} /> <span>External</span>
                                </label>
                            </div>
                            {formData.isExternal ? (
                                <input className="w-full border border-gray-300 p-2 rounded text-sm bg-white" placeholder="Trainer Name" value={formData.externalName} onChange={e => setFormData({...formData, externalName: e.target.value})} />
                            ) : (
                                <div className="relative">
                                    <input className="w-full border border-gray-300 p-2 rounded text-sm" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                    {searchTerm && suggestions.length > 0 && (
                                        <div className="absolute z-10 w-full bg-white border mt-1 shadow-lg max-h-32 overflow-y-auto">
                                            {suggestions.map(u => (
                                                <div key={u.id} onClick={() => addPresenter(u)} className="p-2 hover:bg-blue-50 cursor-pointer text-sm">{u.name}</div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {selectedPresenters.map(p => (
                                            <span key={p.id} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-[10px] flex items-center">
                                                {p.name} <button type="button" onClick={() => setSelectedPresenters(selectedPresenters.filter(x => x.id !== p.id))} className="ml-1">×</button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-full md:w-2/3 border-l border-gray-100 pl-0 md:pl-6 flex flex-col">
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-bold text-gray-700">Enroll Employees</label>
                            <span className="text-xs font-bold bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{enrolledUsers.length} Selected</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Selected employees will see this in their dashboard as 'Upcoming'.</p>
                        
                        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-xl">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                                {Object.entries(departments).map(([dept, users]) => {
                                    const allSelected = users.every(u => enrolledUsers.includes(u.id));
                                    return (
                                        <div key={dept} className="border border-gray-100 rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-3 py-2 flex justify-between items-center border-b border-gray-100">
                                                <span className="text-xs font-bold text-gray-600">{dept}</span>
                                                <button type="button" onClick={() => toggleEnrollDept(dept)} className="text-[10px] text-blue-600 font-bold hover:underline">
                                                    {allSelected ? 'None' : 'All'}
                                                </button>
                                            </div>
                                            <div className="divide-y divide-gray-50">
                                                {users.map(u => (
                                                    <div key={u.id} onClick={() => toggleEnrollUser(u.id)} 
                                                        className={`px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 text-xs ${enrolledUsers.includes(u.id) ? 'bg-blue-50/50' : ''}`}
                                                    >
                                                        <div className="flex items-center">
                                                            <UserAvatar url={u.photoURL} name={u.name} className="h-5 w-5 mr-2"/>
                                                            <span className={enrolledUsers.includes(u.id) ? 'font-bold text-[#213368]' : 'text-gray-600'}>{u.name}</span>
                                                        </div>
                                                        {enrolledUsers.includes(u.id) && <CheckCircleIcon className="h-4 w-4 text-[#20BEC6]" />}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-200 bg-white flex justify-end space-x-3">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition">Cancel</button>
                    <button type="button" onClick={handleSubmit} disabled={isSaving} className="px-6 py-2.5 bg-[#20BEC6] text-white rounded-lg font-bold hover:opacity-90 transition shadow-sm">
                        {isSaving ? 'Saving...' : 'Save & Publish'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// ATTENDANCE MODAL
// ------------------------------------------------------------------
function AttendanceModal({ appId, session, onClose }) {
    const [enrolledList, setEnrolledList] = useState([]);
    const [presentAttendees, setPresentAttendees] = useState(new Set(session.attendeeIds || []));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchEnrolled = async () => {
            if (!session.enrolledIds || session.enrolledIds.length === 0) return;
            
            const chunks = [];
            for (let i = 0; i < session.enrolledIds.length; i += 10) {
                chunks.push(session.enrolledIds.slice(i, i + 10));
            }

            let allUsers = [];
            for (const chunk of chunks) {
                const q = query(collection(db, `artifacts/${appId}/users`), where(documentId(), 'in', chunk));
                const snap = await getDocs(q);
                allUsers = [...allUsers, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))];
            }
            setEnrolledList(allUsers);
        };
        fetchEnrolled();
    }, [appId, session.enrolledIds]);

    const togglePresence = (userId) => {
        const newSet = new Set(presentAttendees);
        if (newSet.has(userId)) newSet.delete(userId);
        else newSet.add(userId);
        setPresentAttendees(newSet);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const finalAttendees = Array.from(presentAttendees);
            const batch = writeBatch(db);

            const sessionRef = doc(db, `artifacts/${appId}/pd_events`, session.id);
            batch.update(sessionRef, { attendeeIds: finalAttendees });

            finalAttendees.forEach(userId => {
                const userObj = enrolledList.find(u => u.id === userId);
		// Use Deterministic ID to ensure we update the correct doc
                const activityRef = doc(db, `artifacts/${appId}/pd_activities`, `${session.id}_${userId}`);
                
                batch.set(activityRef, {
                    userId: userId,
                    managerId: userObj?.managerId || null,
		    linkedEventId: session.id,
                    title: session.title,
                    type: session.category || 'Soft Skills',
                    date: session.date,
                    hours: Number(session.durationHours || 0),
                    status: 'Completed',
                    completedAt: serverTimestamp() 
                }, { merge: true });
            });

            await batch.commit();
            setIsSaving(false);
            onClose();
        } catch (err) {
            console.error(err);
            alert("Error saving attendance: " + err.message);
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[80vh]">
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-xl font-bold text-[#213368]">Mark Attendance</h3>
                    <p className="text-sm text-gray-500 mt-1">{session.title}</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-2">
                    {enrolledList.length === 0 ? (
                        <p className="text-center text-gray-400 py-8">No employees enrolled.</p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {enrolledList.map(u => (
                                <div key={u.id} onClick={() => togglePresence(u.id)} className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${presentAttendees.has(u.id) ? 'bg-green-50' : ''}`}>
                                    <div className="flex items-center">
                                        <UserAvatar url={u.photoURL} name={u.name} className="h-8 w-8 mr-3"/>
                                        <div>
                                            <p className={`text-sm font-bold ${presentAttendees.has(u.id) ? 'text-green-800' : 'text-gray-700'}`}>{u.name}</p>
                                            <p className="text-xs text-gray-400">{u.department}</p>
                                        </div>
                                    </div>
                                    {presentAttendees.has(u.id) ? (
                                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                                    ) : (
                                        <div className="h-6 w-6 rounded-full border-2 border-gray-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-white flex justify-end space-x-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving} className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 shadow-sm">
                        {isSaving ? 'Saving...' : 'Confirm Presence'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeedbackModal({ appId, session, userId, onClose }) {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    const handleSubmit = async (e) => {
        e.preventDefault(); setIsSaving(true);
        try {
            const ref = doc(db, `artifacts/${appId}/pd_events`, session.id);
            await updateDoc(ref, { [`feedbackLog.${userId}`]: { rating, comment, timestamp: new Date().toISOString() } });
            setIsSaving(false); onClose();
        } catch (err) { console.error(err); setIsSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                <h3 className="text-xl font-bold text-[#213368] mb-4">Rate Training</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex justify-center space-x-2">{[1, 2, 3, 4, 5].map(s => <button key={s} type="button" onClick={() => setRating(s)} className={`text-4xl ${s <= rating ? 'text-yellow-400' : 'text-gray-200'}`}>★</button>)}</div>
                    <textarea className="w-full border border-gray-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" rows="3" value={comment} onChange={e => setComment(e.target.value)} placeholder="Comments..." required />
                    <div className="flex justify-end space-x-3 pt-2"><button type="button" onClick={onClose} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button><button type="submit" disabled={isSaving} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg hover:opacity-90">Submit</button></div>
                </form>
            </div>
        </div>
    );
}

function ViewFeedbackModal({ session, onClose }) {
    const feedbackList = session.feedbackLog ? Object.values(session.feedbackLog) : [];
    const avg = feedbackList.length ? (feedbackList.reduce((a, b) => a + b.rating, 0) / feedbackList.length).toFixed(1) : 0;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between"><h3 className="text-xl font-bold text-[#213368]">Feedback</h3><button onClick={onClose}><XIcon/></button></div>
                <div className="p-6 bg-gray-50 border-b border-gray-100 flex justify-center space-x-4"><div className="text-center"><span className="text-4xl font-extrabold text-[#213368]">{avg}</span></div><div className="text-3xl text-yellow-400">★</div><div className="text-center pl-4 border-l"><span className="text-xl font-bold text-gray-600">{feedbackList.length}</span></div></div>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">{feedbackList.length === 0 ? <p className="text-center text-gray-400">No feedback.</p> : feedbackList.map((i, idx) => <div key={idx} className="bg-white border p-4 rounded shadow-sm"><p className="text-sm italic">"{i.comment}"</p></div>)}</div>
            </div>
        </div>
    );
}